import { resendCodeEmail } from "api"
import clsx from "clsx"
import ResendArrowIcon from "components/icons/ResendArrowIcon"
import Tooltip from "components/Tooltip"
import BodyText from "components/Typography/BodyText"
import useHandleApiError from "hooks/useHandleApiErrors"
import { Dispatch, memo, SetStateAction, useCallback, useEffect, useState } from "react"
import { useMutation } from "react-query"
import styled from "styled-components"

const ResendButtonContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: space-between;
`

const ResendButton = styled.button`
  background-color: ${(props) => props.theme.palette.white};
  border-radius: 6px;
  height: 35px;
  width: 121px;
  padding: 8px 12px;

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
    cursor: not-allowed;
  }
`

interface ResendButtonProps {
  token?: string
  email: string
  setError: Dispatch<SetStateAction<string | undefined>>
  setResentEmail: Dispatch<SetStateAction<boolean>>
}

const ResendButtonWithTimer = (props: ResendButtonProps) => {
  const { token, email, setError, setResentEmail } = props

  const [timeLeft, setTimeLeft] = useState(0)
  const [expirationTime, setExpirationTime] = useState("")
  const [disabled, setDisabled] = useState(false)

  const { getErrorMessage } = useHandleApiError()

  const resendCode = useMutation({
    mutationFn: async (data: App.ResendLoginCodePayload) => {
      await resendCodeEmail(data)
    },
    onSuccess: () => {
      setResentEmail(true)
    },
    onError: (error) => {
      const errMsg = getErrorMessage(error, "Não foi possível enviar o código novamente.")

      setError(errMsg)
      setTimeLeft(0)
      setExpirationTime("")
    }
  })

  const handleResendCode = useCallback(() => {
    setResentEmail(false)
    setError(undefined)

    const fiveMinutes = 5 * 60
    const expirationTime = Date.now() + fiveMinutes * 1000

    resendCode.mutate({ token, email })

    setDisabled(true)
    setTimeLeft(fiveMinutes)
    setExpirationTime(expirationTime.toString())
  }, [token, email])

  useEffect(() => {
    const interval = setInterval(() => {
      if (expirationTime) {
        const timeRemaining = Math.max(0, Math.floor((parseInt(expirationTime, 10) - Date.now()) / 1000))

        setTimeLeft(timeRemaining)
        setDisabled(timeRemaining > 0)

        if (timeRemaining === 0) {
          clearInterval(interval)
          setExpirationTime("")
        }
      } else {
        setDisabled(false)
        setTimeLeft(0)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [expirationTime])

  const tooltipText = disabled
    ? `Reenvio disponível em ${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, "0")}`
    : "Reenviar código de confirmação"

  return (
    <ResendButton onClick={handleResendCode} disabled={disabled} className={clsx({ disabled })}>
      <ResendButtonContainer>
        <Tooltip text={tooltipText}>
          <BodyText size="medium" weight="bold">
            Reenviar
          </BodyText>
        </Tooltip>
        <ResendArrowIcon isDisabled={disabled} />
      </ResendButtonContainer>
    </ResendButton>
  )
}

export default memo(ResendButtonWithTimer)
